/**
 * @file        src/pages/wallet/WalletAuditLog.jsx
 * @description Wallet Recharge Log — who credited/refunded/froze/enabled which
 *              wallet, when, and why. Reads /api/audit-logs (defaults to the
 *              wallet.* action family).
 * @author      Kinetika Labs
 * @created     2026-07-27
 * @changelog
 *   - 2026-07-27 Claude — Initial (Wallet Recharge Log).
 *   - 2026-08-03 Claude — Crumb: Account Management -> Wallet Management (new
 *                          sidebar group).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ACTIONS = ['wallet.credit', 'wallet.refund', 'wallet.freeze', 'wallet.enable', 'wallet.close'];
const ACTION_LABEL = {
  'wallet.credit': 'Money added', 'wallet.refund': 'Refund posted',
  'wallet.freeze': 'Wallet frozen', 'wallet.enable': 'Wallet unfrozen', 'wallet.close': 'Wallet closed',
};
const ACTION_TONE = {
  'wallet.credit': 'ok', 'wallet.refund': 'ok', 'wallet.freeze': 'warn', 'wallet.enable': 'ok', 'wallet.close': 'rej',
};

export default function WalletAuditLog() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [action, setAction] = useState('');

  const params = { page, pageSize, ...(action ? { action } : {}) };
  const { data, isLoading } = useQuery({
    queryKey: ['wallet-audit-log', params],
    queryFn: () => get('/audit-logs', { params }),
    placeholderData: keepPreviousData,
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <>
      <PageHead
        title="Wallet Recharge Log"
        subtitle="Wallet Management · every admin credit, refund, freeze & unfreeze"
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => e.preventDefault()}>
          <div className="field">
            <label>Action</label>
            <Select
              value={action}
              onChange={(v) => { setAction(v); setPage(1); }}
              options={[{ value: '', label: 'All wallet actions' }, ...ACTIONS.map((a) => ({ value: a, label: ACTION_LABEL[a] }))]}
            />
          </div>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>When</th><th>Admin</th><th>Action</th><th>Target</th><th>Amount</th><th>Reason</th><th>Balance after</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No wallet activity yet.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{new Date(r.createdAt).toLocaleString('en-IN')}</td>
                    <td>{r.actorName}</td>
                    <td><Chip tone={ACTION_TONE[r.action] || 'pend'}>{ACTION_LABEL[r.action] || r.action}</Chip></td>
                    <td className="mono">{r.targetType ? `${r.targetType} #${r.targetId}` : '—'}</td>
                    <td className="mono">{r.meta?.amount != null ? inr(r.meta.amount) : '—'}</td>
                    <td>{r.meta?.reason || <span className="muted">—</span>}</td>
                    <td className="mono">{r.meta?.balanceAfter != null ? inr(r.meta.balanceAfter) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
